import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { AccountClientSortCol, TAccountClientList, TAccountClientListResponse, TAccountClientQuery, TAccountClientListSortCol, adaptAccountClientFromListResponse, adaptAccountClientListQueryToRequest } from '../../types';
import { BACKEND_ACCOUNT_CLIENT } from '../../backend-paths';


export const accountClientListGetApi = createAsyncThunk(
	BACKEND_ACCOUNT_CLIENT,
	(payload: TAccountClientQuery | undefined, thunkApi) => {
		const params = payload ? adaptAccountClientListQueryToRequest(payload) : {};

		return HttpService.get<TAccountClientListResponse>(BACKEND_ACCOUNT_CLIENT, { params }).then(result => {
			return adaptAccountClientFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean,
	list: TAccountClientList,
	query: TAccountClientQuery,
	error?: HttpError,
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: AccountClientSortCol.CREATED_AT,
	},
	error: undefined,
};

export const accountClientListSlice = createSlice({
	name: 'accountClientListSlice',
	initialState,
	reducers: {
		setAccountClientListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setAccountClientListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setAccountClientListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setAccountClientListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setAccountClientListQuerySortCol(state, action: PayloadAction<TAccountClientListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearAccountClientList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[accountClientListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[accountClientListGetApi.fulfilled.type]: (state, action: PayloadAction<TAccountClientList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[accountClientListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useAccountClientListSelector = () => useAppSelector(state => state[accountClientListSlice.name]);

export const { clearAccountClientList, setAccountClientListQueryLimit, setAccountClientListQueryOffset, setAccountClientListQuerySort, setAccountClientListQuerySortCol, setAccountClientListQuerySearch } = accountClientListSlice.actions;
